import { TranslateService } from '@ngx-translate/core';
import {
  NotificationActionButton,
  NotificationActionButtonsConfig,
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationToasterService,
} from '../services/notification-toaster.service';
import { ERRORS, REQUESTS } from '../common/utils/notification-constants';
import { DevicesManagerService } from '../services/devices-manager.service';
import { DEVICE } from '../sessions/panel/participants-manager/participants-manager.component';
import { intercomArticles } from '../intercom-articles';
import { CustomerSupportService } from '../services/customer-support.service';
import { IconTypes } from '../standalones/components/pencil-icon/pencil-icon.component';

export class DevicesNotifications {
  constructor(
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private devicesManagerService: DevicesManagerService,
    private customerSupportService: CustomerSupportService,
  ) {}

  showGrantMicAccessNotification(): void {
    this.showGrantDeviceAccessNotification(
      'mic',
      this.translateService.instant('Grant microphone access'),
      ERRORS.GRANT_MICROPHONE_ACCESS,
      this.translateService.instant(
        'Please enable microphone access in your browser settings to join with audio.',
      ),
    );
  }

  showGrantCamAccessNotification(): void {
    this.showGrantDeviceAccessNotification(
      'videocam',
      this.translateService.instant('Grant camera access'),
      ERRORS.GRANT_CAMERA_ACCESS,
      this.translateService.instant(
        'Please enable camera access in your browser settings to join with video.',
      ),
    );
  }

  showGrantScreenshareAccessNotification() {
    const helpButton: NotificationActionButton = {
      text: this.translateService.instant('Need help?'),
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
        outlined: true,
      },
      onClick: () => {
        this.customerSupportService.openLink(intercomArticles.HowToEnableScreenSharingPermissions);
      },
      close: false,
    };

    const notificationIcon: NotificationIconConfig = {
      assetName: 'screen_share',
      size: 16,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: helpButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translateService.instant('Screenshare access denied'),
      this.translateService.instant(
        "Please enable screen sharing permissions for your browser in your computer's System Preferences.",
      ),
      ERRORS.GRANT_SCREENSHARE_ACCESS,
      notificationIcon,
      buttons,
    );
  }

  showScreenshareFailedNotification() {
    const helpButton: NotificationActionButton = {
      text: this.translateService.instant('Need help?'),
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
        outlined: true,
      },
      onClick: () => {
        this.customerSupportService.openLink(intercomArticles.WhatToDoWhenYourScreenShareFails);
      },
      close: false,
    };

    const notificationIcon: NotificationIconConfig = {
      assetName: 'screen_share',
      size: 16,
      outlined: true,
      type: NotificationIconTypes.MAT_ICON,
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: helpButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translateService.instant('Screenshare failed'),
      this.translateService.instant(
        'Something went wrong while trying to start the screenshare. Please try again. If you continue to run into issues, please restart your browser.',
      ),
      ERRORS.GRANT_SCREENSHARE_ACCESS,
      notificationIcon,
      buttons,
    );
  }

  private showGrantDeviceAccessNotification(
    titleIcon: string,
    titleMsg: string,
    notificationCode: string,
    bodyMsg: string,
  ) {
    const helpButton: NotificationActionButton = {
      text: this.translateService.instant('Need help?'),
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
        outlined: true,
      },
      onClick: () => {
        this.customerSupportService.openLink(
          intercomArticles.SettingUpYourVideoAndMicrophonePermissionsInSpaces,
        );
      },
      close: false,
    };

    const notificationIcon: NotificationIconConfig = {
      assetName: titleIcon,
      size: 16,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: helpButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      titleMsg,
      bodyMsg,
      notificationCode,
      notificationIcon,
      buttons,
    );
  }

  showCamNotFoundNotification(): void {
    this.showDeviceNotFoundNotification(
      this.translateService.instant('Camera not found'),
      this.translateService.instant(
        'Please verify that your camera is connected correctly, or select another camera in device settings.',
      ),
      ERRORS.CAMERA_NOT_FOUND,
    );
  }

  showMicNotFoundNotification(): void {
    this.showDeviceNotFoundNotification(
      this.translateService.instant('Microphone not found'),
      this.translateService.instant(
        'Please verify that your mic is connected correctly or select another microphone in device settings.',
      ),
      ERRORS.MICROPHONE_NOT_FOUND,
    );
  }

  private showDeviceNotFoundNotification(
    titleMsg: string,
    bodyMsg: string,
    notificationCode: string,
  ): void {
    const settingsButton: NotificationActionButton = {
      text: this.translateService.instant('Settings'),
      icon: 'settings',
      iconConfig: {
        type: IconTypes.matIcon,
      },
      onClick: () => this.devicesManagerService.openDeviceSelection(),
      close: false,
    };

    const needHelpButton: NotificationActionButton = {
      text: this.translateService.instant('Need help?'),
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
        outlined: true,
      },
      onClick: () => {
        this.customerSupportService.openLink(
          intercomArticles.HowToChangeYourCameraAndMicrophoneInSpaces,
        );
      },
      close: false,
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: settingsButton,
      secondary: needHelpButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      titleMsg,
      bodyMsg,
      notificationCode,
      undefined,
      buttons,
    );
  }

  showCamInUseByAnotherAppNotification(): void {
    this.showDeviceInUseByAnotherAppNotification(
      this.translateService.instant('No camera input detected'),
      this.translateService.instant(
        'Please check that your camera is configured correctly and is not being used by another application.',
      ),
      ERRORS.NO_CAMERA_INPUT_DETECTED,
    );
  }

  showMicInUseByAnotherAppNotification(): void {
    this.showDeviceInUseByAnotherAppNotification(
      this.translateService.instant('Microphone not sending audio'),
      this.translateService.instant(
        'Please check that your microphone is configured correctly and is not being used by another application.',
      ),
      ERRORS.MICROPHONE_NOT_SENDING_AUDIO,
    );
  }

  private showDeviceInUseByAnotherAppNotification(
    titleMsg: string,
    bodyMsg: string,
    notificationCode: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ) {
    const settingsButton: NotificationActionButton = {
      text: this.translateService.instant('Settings'),
      icon: 'settings',
      iconConfig: {
        type: IconTypes.matIcon,
      },
      onClick: () => this.devicesManagerService.openDeviceSelection(),
      close: false,
    };

    const needHelpButton: NotificationActionButton = {
      text: this.translateService.instant('Need help?'),
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
        outlined: true,
      },
      onClick: () => {
        this.customerSupportService.openLink(
          intercomArticles.SettingUpYourVideoAndMicrophonePermissionsInSpaces,
        );
      },
      close: false,
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: settingsButton,
      secondary: needHelpButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      titleMsg,
      this.translateService.instant(bodyMsg),
      notificationCode,
      undefined,
      buttons,
    );
  }

  getDeviceNotificationCodes(device: DEVICE): string[] {
    switch (device) {
      case DEVICE.MIC:
        return [
          ERRORS.GRANT_MICROPHONE_ACCESS,
          ERRORS.MICROPHONE_NOT_FOUND,
          ERRORS.MICROPHONE_NOT_SENDING_AUDIO,
          ERRORS.FAILED_TO_START_MICROPHONE,
          REQUESTS.REQUEST_TO_UNMUTE,
        ];
      case DEVICE.CAM:
        return [
          ERRORS.GRANT_CAMERA_ACCESS,
          ERRORS.CAMERA_NOT_FOUND,
          ERRORS.NO_CAMERA_INPUT_DETECTED,
          ERRORS.FAILED_TO_START_CAMERA,
          REQUESTS.REQUEST_TO_ENABLE_CAMERA,
        ];
    }
  }

  showCamGeneralErrorNotification(): void {
    this.showDeviceGeneralErrorNotification(
      this.translateService.instant('Failed to start camera'),
      this.translateService.instant(
        'Verify that your camera is set-up correctly and try again. If issues persist, restart your browser or select another camera in device settings.',
      ),
      ERRORS.FAILED_TO_START_CAMERA,
    );
  }

  showMicGeneralErrorNotification(): void {
    this.showDeviceGeneralErrorNotification(
      this.translateService.instant('Failed to start microphone'),
      this.translateService.instant(
        'Verify that your microphone is set-up correctly and try again. If issues persist, restart your browser or select another microphone in device settings.',
      ),
      ERRORS.FAILED_TO_START_MICROPHONE,
    );
  }

  showDeviceGeneralErrorNotification(
    titleMsg: string,
    bodyMsg: string,
    notificationCode: string,
  ): void {
    const settingsButton: NotificationActionButton = {
      text: this.translateService.instant('Settings'),
      icon: 'settings',
      iconConfig: {
        type: IconTypes.matIcon,
      },
      onClick: () => this.devicesManagerService.openDeviceSelection(),
      close: false,
    };

    const needHelpButton: NotificationActionButton = {
      text: this.translateService.instant('Need help?'),
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
        outlined: true,
      },
      onClick: () => {
        this.customerSupportService.openLink(
          intercomArticles.SettingUpYourVideoAndMicrophonePermissionsInSpaces,
        );
      },
      close: false,
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: settingsButton,
      secondary: needHelpButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      titleMsg,
      this.translateService.instant(bodyMsg),
      notificationCode,
      undefined,
      buttons,
    );
  }
}
